import type { ExtendedObservation } from '@seen/core/models/observation'
import type { JSX } from 'react'
import { ResourceFieldFormBatch } from '#src/components/core/form/ResourceFieldFormBatch.tsx'
import { ObservationRichTextEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationRichTextEditor.tsx'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'

// One note for a multi-select capture, written onto every answer so the set
// stays together.
export function ObservationAnswerSetNote({
  observations,
  singleLine = false,
  autofocus = false,
  disabled = false,
  onDraftNote,
  className,
}: {
  observations: ExtendedObservation[]
  disabled?: boolean
  singleLine?: boolean
  autofocus?: boolean
  // Given when the observations are drafts held in the note: the note is
  // handed back to write onto every draft instead of patching records.
  onDraftNote?: (value: string) => void
  className?: string
}): JSX.Element | null {
  const primary = observations[0]
  const practitioner = useCurrentPractitioner()
  if (!primary) {
    return null
  }
  if (onDraftNote) {
    const current = primary.noteFirstText ?? ''
    return (
      <ObservationRichTextEditor
        placeholder="Notes…"
        singleLine={singleLine}
        autofocus={autofocus ? 'end' : false}
        value={current}
        onChange={(value) => {
          if (value !== current) {
            onDraftNote(value)
          }
        }}
        className={className}
      />
    )
  }
  return (
    <ResourceFieldFormBatch
      instance={primary}
      valueFn={(observation) => observation.noteFirstText}
      debounceTyping
      disabled={disabled}
      builderFn={(builder, value) => {
        for (const observation of observations) {
          builder.patch(observation, (record) => {
            record.setNoteFirst(value ?? undefined, practitioner.reference)
            record.performerPractitioner = practitioner.reference
          })
        }
      }}
      render={({ field }) => (
        <ObservationRichTextEditor
          placeholder="Notes…"
          singleLine={singleLine}
          autofocus={autofocus ? 'end' : false}
          {...field}
        />
      )}
      className={className}
    />
  )
}
